import { sectionRenderer } from '@theme/section-renderer';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { getScrollTop, scrollTo } from '@theme/scroll-container';
import { requestIdleCallback } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * Load more (THEME.md §7.11 **Load more**, §9.4 #36; D-114 (3), T-183; DESIGN.md › Layout › Collection page): the
 * element of `snippets/okt-load-more.liquid`, under the product grid of the Collection and Search results sections
 * while Auto-load products is off and Pagination is "Show more button".
 *
 * - "Show more" fetches the next page through the Section Rendering API with Horizon's `sectionRenderer` (so it gets
 *   the page Horizon's paginated list prefetched, and the page after it is fetched while idle) and appends its cards,
 *   as Horizon's infinite scroll does. The line and the bar take the fetched page's, which Liquid writes in the
 *   shop's language with the plural of the total; on the last page the button goes.
 * - Focus moves to the first new card's link, without scrolling (the card sits where the button was), and the line is
 *   a polite live region (`role="status"`), so the new count is read out.
 * - The URL keeps the page shown last (`?page=N`, replaced, not pushed, as on fabini.cz, so Back leaves the list). A
 *   reload, or a return the back-forward cache didn't keep, gets that page from Liquid: the pages before it are
 *   fetched and put back before it, and the scroll position at leaving applies again, unless the visitor has
 *   scrolled meanwhile; without one, what was in view stays put, as when Horizon's infinite scroll puts a previous
 *   page back. The position is kept in sessionStorage on `pagehide`: Horizon's copy in `history.state`
 *   (`scroll-container.js`) is written on `pagehide` too, which a reload doesn't keep. While the URL is beyond page 1
 *   the entry's own scroll restoration is off (`manual`, as Horizon has it from 990 px): it would act on page N alone.
 * - Filters and sorting re-render the section (Horizon's facets drop `page`), which brings back page 1's line, bar and
 *   button; a page still loading for the old results is dropped.
 * - Without JavaScript the link to the next page shows instead of the button (CSS, `:defined`).
 */

/** The cards of a grid, as Horizon's paginated list reads them */
const CARDS = ':scope > [ref="cards[]"]';

/** A fetched page's cards and, on page 1, its grid promos (T-189, D-117), in their order: a reload of ?page=N puts
 * page 1's promos back where they were. Promos aren't cards, so counts and pages still read CARDS. */
const ITEMS = ':scope > :is([ref="cards[]"], .okt-grid-promo)';

/** The list's results without the page: a fetched page must still belong to them */
function resultsKey() {
  const url = new URL(window.location.href);
  url.searchParams.delete('page');
  url.searchParams.sort();
  url.hash = '';
  return url.toString();
}

/**
 * The URL of a page of the current results, built as Horizon's paginated list builds it
 * @param {number} page
 */
function pageUrl(page) {
  const url = new URL(window.location.href);
  url.searchParams.set('page', String(page));
  url.hash = '';
  return url;
}

/** Where a list beyond its first page was scrolled when the visitor left it: `{ url, top }` */
const SCROLL_KEY = 'okt-load-more-scroll';

window.addEventListener('pagehide', () => {
  if (!(Number(new URL(window.location.href).searchParams.get('page')) > 1)) return;
  try {
    sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ url: window.location.href, top: getScrollTop() }));
  } catch {
    // Without storage a return keeps what was in view
  }
});

/**
 * The entry's own scroll restoration: off while its URL is beyond page 1, as #restore puts the pages back first and
 * then the position (the browser's acts on page N alone, gliding with the root's smooth scrolling); else Horizon's
 * (`scroll-container.js`: off from 990 px, the browser's below). An entry's mode counts when the page arrives, so it
 * is set as the URL changes: on Show more, and on filters and sorting, whose new entry takes the old one's mode.
 */
function followPage() {
  const beyondFirst = Number(new URL(window.location.href).searchParams.get('page')) > 1;
  history.scrollRestoration = beyondFirst || window.matchMedia('(min-width: 990px)').matches ? 'manual' : 'auto';
}

document.addEventListener(StandardEvents.collectionUpdate, followPage);
document.addEventListener(StandardEvents.searchUpdate, followPage);

/** @returns {number | null} The position kept for this URL, else Horizon's */
function savedScrollTop() {
  try {
    const kept = JSON.parse(sessionStorage.getItem(SCROLL_KEY) ?? 'null');
    if (kept?.url === window.location.href && typeof kept.top === 'number') return kept.top;
  } catch {
    // Horizon's, if any
  }
  const top = history.state?.scrollTop;
  return typeof top === 'number' ? top : null;
}

class OktLoadMore extends HTMLElement {
  #loading = false;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatio;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);

    // In the theme editor Horizon fixes the photo ratio of added cards; outside it the helper does nothing
    const templateCard = this.#list?.querySelector('[ref="cardGallery"]');
    if (templateCard instanceof HTMLElement) {
      this.#aspectRatio = new PaginatedListAspectRatioHelper({ templateCard });
    }

    this.#restore();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  get #list() {
    return this.closest('results-list');
  }

  /** @returns {HTMLElement | null} */
  get #grid() {
    return this.#list?.querySelector('[ref="grid"]') ?? null;
  }

  get #sectionId() {
    return this.#list?.getAttribute('section-id') ?? null;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (event.target instanceof Element && event.target.closest('.okt-load-more__button')) this.#loadNext();
  };

  async #loadNext() {
    const grid = this.#grid;
    const sectionId = this.#sectionId;
    if (this.#loading || !grid || !sectionId) return;

    const cards = grid.querySelectorAll(CARDS);
    const next = Number(cards[cards.length - 1]?.getAttribute('data-page')) + 1;
    const lastPage = Number(grid.dataset.lastPage);
    if (!(next <= lastPage)) return;

    const key = resultsKey();
    const url = pageUrl(next);
    this.#loading = true;
    this.setAttribute('aria-busy', 'true');

    try {
      // The renderer adds section_id to the URL it gets, so it gets a copy
      const html = await sectionRenderer.getSectionHTML(sectionId, true, new URL(url));
      if (!this.isConnected || resultsKey() !== key) return;

      const page = new DOMParser().parseFromString(html, 'text/html');
      const added = this.#cardsOf(page);
      grid.append(...added);
      this.#aspectRatio?.processNewElements();
      this.#take(page.querySelector('okt-load-more'));
      history.replaceState(history.state, '', url.toString());
      followPage();

      const link = added[0]?.querySelector('[ref="productCardLink"], a[href]');
      if (link instanceof HTMLElement) link.focus({ preventScroll: true });

      if (next < lastPage) {
        requestIdleCallback(() => {
          sectionRenderer.getSectionHTML(sectionId, true, pageUrl(next + 1)).catch(() => {});
        });
      }
    } catch {
      // The button stays for another try
    } finally {
      this.#loading = false;
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * The new cards of a fetched page, with page 1's grid promos. A card already in the list (the order shifted between
   * two pages) isn't added twice.
   * @param {Document} page
   * @returns {Element[]}
   */
  #cardsOf(page) {
    const cards = page.querySelector('[ref="grid"]')?.querySelectorAll(ITEMS) ?? [];
    return [...cards].filter((card) => !card.id || !document.getElementById(card.id));
  }

  /**
   * The line, the bar and the link of the page just added, as Liquid wrote them; the last page has no link, so the
   * button goes too.
   * @param {Element | null} fetched - The fetched page's element
   */
  #take(fetched) {
    if (!fetched) return;

    const status = this.querySelector('.okt-load-more__status');
    const text = fetched.querySelector('.okt-load-more__status')?.textContent;
    if (status && text) status.textContent = text;

    const bar = this.querySelector('.okt-load-more__bar');
    const share = fetched.querySelector('.okt-load-more__bar')?.getAttribute('style');
    if (bar && share) bar.setAttribute('style', share);

    const href = fetched.querySelector('.okt-load-more__link')?.getAttribute('href');
    const link = this.querySelector('.okt-load-more__link');
    if (href && link) {
      link.setAttribute('href', href);
    } else {
      for (const action of this.querySelectorAll('.okt-load-more__link, .okt-load-more__button')) action.remove();
    }
  }

  /**
   * A reload of `?page=N`, or a return the back-forward cache didn't keep: Liquid drew page N alone, so the pages
   * before it come back before it.
   */
  async #restore() {
    const grid = this.#grid;
    const sectionId = this.#sectionId;
    const first = grid?.querySelector(CARDS);
    const page = Number(first?.getAttribute('data-page'));
    if (!grid || !sectionId || !first || !(page > 1)) return;

    // Also for an entry that arrived with ?page=N, for its next reload
    followPage();

    const key = resultsKey();

    // A visitor who scrolls or taps meanwhile keeps their place
    let moved = false;
    const watch = new AbortController();
    for (const type of ['wheel', 'touchmove', 'keydown', 'pointerdown']) {
      window.addEventListener(type, () => (moved = true), { passive: true, signal: watch.signal });
    }

    try {
      const pages = await Promise.all(
        Array.from({ length: page - 1 }, (_, index) =>
          sectionRenderer.getSectionHTML(sectionId, true, pageUrl(index + 1))
        )
      );
      if (!this.isConnected || resultsKey() !== key || first.parentElement !== grid) return;

      const cards = pages.flatMap((html) => this.#cardsOf(new DOMParser().parseFromString(html, 'text/html')));
      const top = getScrollTop();
      const before = first.getBoundingClientRect().top + top;
      grid.prepend(...cards);
      this.#aspectRatio?.processNewElements();

      const saved = savedScrollTop();
      if (!moved && saved !== null) {
        // Horizon restores history.state's position on pageshow, which can come later: the same one
        const state = typeof history.state === 'object' && history.state !== null ? history.state : {};
        history.replaceState({ ...state, scrollTop: saved }, '');
        scrollTo({ top: saved, behavior: 'instant' });
      } else {
        scrollTo({ top: top + first.getBoundingClientRect().top + getScrollTop() - before, behavior: 'instant' });
      }
    } catch {
      // Page N stays alone, with its line and the button for the next page
    } finally {
      watch.abort();
    }
  }
}

if (!customElements.get('okt-load-more')) {
  customElements.define('okt-load-more', OktLoadMore);
}
